import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'wouter';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, X, Send, Loader2, Bot, CheckCircle2, Sparkles, Trash2, Plus, Edit2, Globe, AlertTriangle, Zap, ArrowUpDown, DollarSign, RotateCcw, MapPin } from 'lucide-react';
import { apiRequest, getAuthHeaders } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';

// Matches the current path against /projects/:id/* or /ai-workflow/:id so the
// globally-mounted widget (client/src/App.tsx) can tell which project (if
// any) is active without every page having to pass projectId as a prop.
function projectIdFromPath(path: string): string | null {
  const match = path.match(/^\/(?:projects|ai-workflow)\/([^/]+)/);
  return match ? match[1] : null;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface PendingAction {
  type: 'update_scope_item' | 'add_scope_item' | 'remove_scope_item' | 'set_scope_item_cost' | 'update_project_field';
  label: string;
  isDestructive: boolean;
  scopeDraftId?: string | null;
  // fields for /execute
  itemId?: string | null;
  itemIndex?: number | null;
  oldItem?: any;
  newItem?: any;
  field?: string;
  value?: string;
  // backward-compat for remove
  description?: string;
  item_description?: string;
}

interface UndoSnapshot {
  scopeDraftId: string;
  scopeItems: any[];
}

interface ProjectAssistantProps {
  // Optional: when mounted globally (App.tsx) with no explicit projectId,
  // the widget derives one from the current URL instead.
  projectId?: string;
  project?: any;
  intelligence?: any;
  embedded?: boolean;
}

function actionIcon(type: string) {
  if (type === 'add_scope_item') return <Plus className="h-3 w-3" />;
  if (type === 'update_scope_item') return <ArrowUpDown className="h-3 w-3" />;
  if (type === 'set_scope_item_cost') return <DollarSign className="h-3 w-3" />;
  if (type === 'update_project_field') return <Edit2 className="h-3 w-3" />;
  return <CheckCircle2 className="h-3 w-3" />;
}

export function ProjectAssistant({ projectId, project, intelligence, embedded = false }: ProjectAssistantProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, navigate] = useLocation();

  // projectId is only passed explicitly by the two page-level embeds
  // (ScopeReviewEditor, ExportDashboard). The globally-mounted instance
  // (App.tsx) passes neither and relies on the URL instead, so the widget
  // still gets full project context on any /projects/:id page.
  const detectedProjectId = projectId ?? projectIdFromPath(location);
  const effectiveProjectId = detectedProjectId ?? null;

  // Only fetched when the widget had to detect the project itself (global
  // mount) — the page-level embeds already have the full object loaded and
  // pass it in, so this stays disabled for them.
  const { data: fetchedProject } = useQuery<any>({
    queryKey: ['/api/projects', effectiveProjectId],
    queryFn: async () => {
      const res = await fetch(`/api/projects/${effectiveProjectId}`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch project');
      return res.json();
    },
    enabled: !!effectiveProjectId && !project,
    staleTime: 60_000,
  });
  const effectiveProject = project ?? fetchedProject ?? null;

  const STORAGE_KEY = `ra-chat-${effectiveProjectId ?? 'global'}`;
  const SESSION_KEY = `ra-chat-${effectiveProjectId ?? 'global'}-sid`;

  const [isOpen, setIsOpen] = useState(false);
  // Initialise synchronously from localStorage so the greeting effect sees
  // the real history on first render and doesn't overwrite it.
  const [messages, setMessages] = useState<Message[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch { /* ignore */ }
    return [];
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [pendingActions, setPendingActions] = useState<PendingAction[]>([]);
  const [executingActions, setExecutingActions] = useState(false);
  const [undoSnapshot, setUndoSnapshot] = useState<UndoSnapshot | null>(null);
  const [suggestedNavigation, setSuggestedNavigation] = useState<{ path: string; label: string } | null>(null);
  const undoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const hydratedRef = useRef(false);

  // sessionId is the audit-log correlation key. The server tags every audit
  // row with it so a session can be reconstructed end-to-end. It is generated
  // lazily on first send, persisted in localStorage so refresh doesn't lose
  // it, and cleared on session-end (panel close / clear / unmount).
  const sessionIdRef = useRef<string | null>(null);
  const sessionStartedRef = useRef(false);

  const generateSessionId = (): string => {
    try {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
      }
    } catch { /* fall through */ }
    return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}-${Math.random().toString(36).slice(2, 10)}`;
  };

  const endSession = (reason: 'close' | 'clear' | 'unmount' | 'pagehide') => {
    const sid = sessionIdRef.current;
    // The help endpoint (no active project) has no session-start/end concept.
    if (!sid || !sessionStartedRef.current || !effectiveProjectId) return;
    try {
      fetch(`/api/v1/projects/${effectiveProjectId}/assistant/session-end`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ sessionId: sid, reason }),
        keepalive: true,
      }).catch(() => { /* swallow — best effort */ });
    } catch { /* ignore */ }
    try { localStorage.removeItem(SESSION_KEY); } catch { /* ignore */ }
    sessionIdRef.current = null;
    sessionStartedRef.current = false;
  };

  // Load saved history + sessionId once per projectId
  useEffect(() => {
    hydratedRef.current = false;
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
        }
      }
    } catch {
      localStorage.removeItem(STORAGE_KEY);
    }
    try {
      const savedSid = localStorage.getItem(SESSION_KEY);
      if (savedSid && savedSid.length > 0 && savedSid.length <= 64) {
        sessionIdRef.current = savedSid;
        sessionStartedRef.current = true;
      }
    } catch { /* ignore */ }
    hydratedRef.current = true;
  }, [STORAGE_KEY, SESSION_KEY]);

  // Persist on every change (skip before hydration to avoid overwriting with [])
  useEffect(() => {
    if (!hydratedRef.current) return;
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(messages)); } catch { /* quota */ }
  }, [messages, STORAGE_KEY]);

  // End the session on unmount and on pagehide (tab close / iOS Safari).
  useEffect(() => {
    const onPageHide = () => endSession('pagehide');
    window.addEventListener('pagehide', onPageHide);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      endSession('unmount');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveProjectId]);

  const greeting = effectiveProjectId
    ? `Hi! I'm your project assistant for ${effectiveProject?.clientName || 'this project'}. I have full access to this job — scope, photos, moisture readings, and documentation.\n\nTell me what to change and I'll apply the edits right away. Examples:\n• "Change air movers to 4"\n• "Add 32 LF of baseboard replacement at $12/LF"\n• "What does S500 say about Category 2 water?"\n\nDestructive actions (removals) show a confirmation card first.`
    : `Hi! I'm the RestoreAI app assistant. I can help you find your way around and understand the workflow — ask me things like:\n• "Where do I upload photos?"\n• "What happens after scope generation?"\n• "How do I get to Settings?"\n\nOpen a specific project for scope-editing and job-specific questions.`;

  useEffect(() => {
    if ((isOpen || embedded) && messages.length === 0) {
      setMessages([{ role: 'assistant', content: greeting }]);
    }
  }, [isOpen, embedded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, pendingActions, undoSnapshot, suggestedNavigation]);

  const invalidateProject = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['/api/projects', effectiveProjectId] });
    queryClient.invalidateQueries({ queryKey: ['/api/projects', effectiveProjectId, 'scopes'] });
    queryClient.invalidateQueries({ queryKey: [`/api/projects/${effectiveProjectId}`] });
  }, [queryClient, effectiveProjectId]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || isLoading) return;

    const newMessages: Message[] = [...messages, { role: 'user', content: text }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);
    setPendingActions([]);
    setSuggestedNavigation(null);
    // Clear undo once user sends a new message
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoSnapshot(null);

    try {
      let data: any;
      if (effectiveProjectId) {
        // Lazily start a session on the first send. isNewSession is true only
        // for this first request; the server emits assistant_session_start
        // (deduped).
        let isNewSession = false;
        if (!sessionIdRef.current) {
          sessionIdRef.current = generateSessionId();
          isNewSession = true;
          sessionStartedRef.current = true;
          try { localStorage.setItem(SESSION_KEY, sessionIdRef.current); } catch { /* ignore */ }
        }

        data = await apiRequest('POST', `/api/v1/projects/${effectiveProjectId}/assistant`, {
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
          sessionId: sessionIdRef.current,
          isNewSession,
        });

        if (data.pendingActions?.length > 0) {
          setPendingActions(data.pendingActions);
        }
        // project_field updates are still immediate — refresh if any were applied
        if (data.actionsApplied?.some((a: any) => a.type === 'update_project_field')) {
          invalidateProject();
        }
      } else {
        // No active project (globally-mounted widget on a project-less page) —
        // pure navigation/workflow Q&A, no scope tools, no session bookends.
        data = await apiRequest('POST', '/api/v1/assistant/help', {
          messages: newMessages.map(m => ({ role: m.role, content: m.content })),
        });
      }

      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);
      if (data.suggestedNavigation) {
        setSuggestedNavigation(data.suggestedNavigation);
      }
    } catch {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I ran into an issue. Please try again.',
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyAll = async () => {
    if (pendingActions.length === 0) return;
    setExecutingActions(true);
    try {
      const result = await apiRequest('POST', `/api/v1/projects/${effectiveProjectId}/assistant/execute`, {
        actions: pendingActions,
        sessionId: sessionIdRef.current,
      });

      invalidateProject();

      const appliedCount = result.applied?.filter((a: any) => a.status === 'applied').length ?? 0;
      const noun = appliedCount === 1 ? 'change' : 'changes';
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `✓ ${appliedCount} ${noun} applied successfully.`,
      }]);
      setPendingActions([]);

      // Store snapshot for undo (30s window)
      if (result.snapshot) {
        setUndoSnapshot(result.snapshot);
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        undoTimerRef.current = setTimeout(() => setUndoSnapshot(null), 30000);
      }

      toast({ title: `${appliedCount} ${noun} applied`, description: 'Scope updated. Export will reflect the latest changes.' });
    } catch {
      toast({ title: 'Could not apply changes', description: 'Please try again or edit manually in the scope editor.', variant: 'destructive' });
    } finally {
      setExecutingActions(false);
    }
  };

  const handleCancelAll = () => {
    setPendingActions([]);
    setMessages(prev => [...prev, { role: 'assistant', content: 'Changes cancelled. Let me know if you want to make different edits.' }]);
  };

  const handleUndo = async () => {
    if (!undoSnapshot) return;
    try {
      await apiRequest('POST', `/api/v1/projects/${effectiveProjectId}/assistant/undo`, {
        scopeDraftId: undoSnapshot.scopeDraftId,
        scopeItems: undoSnapshot.scopeItems,
      });
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoSnapshot(null);
      invalidateProject();
      setMessages(prev => [...prev, { role: 'assistant', content: 'Undone — scope restored to its previous state.' }]);
      toast({ title: 'Undone', description: 'Scope reverted.' });
    } catch {
      toast({ title: 'Undo failed', variant: 'destructive' });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const nonDestructive = pendingActions.filter(a => !a.isDestructive);
  const destructive = pendingActions.filter(a => a.isDestructive);

  // ── Shared inner content (messages + pending + input) ─────────────────────
  const chatContent = (
    <>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-0">
        {messages.map((msg, idx) => (
          <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[88%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user' ? 'rounded-br-sm' : 'rounded-bl-sm'
              }`}
              style={msg.role === 'user' ? {
                backgroundColor: 'var(--restore-accent)',
                color: '#0a0a0a',
              } : {
                backgroundColor: 'var(--restore-card-bg)',
                color: 'var(--restore-text-primary)',
                border: '1px solid var(--restore-border)',
              }}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm px-4 py-2.5 flex items-center gap-2" style={{ backgroundColor: 'var(--restore-card-bg)', border: '1px solid var(--restore-border)' }}>
              <Loader2 className="h-3.5 w-3.5 animate-spin" style={{ color: 'var(--restore-accent)' }} />
              <span className="text-xs" style={{ color: 'var(--restore-text-muted)' }}>Thinking...</span>
            </div>
          </div>
        )}

        {/* ── Pending actions confirmation card ─────────────────────────────── */}
        {pendingActions.length > 0 && !isLoading && (
          <div className="rounded-xl border overflow-hidden" style={{ borderColor: 'var(--restore-border)', backgroundColor: 'var(--restore-card-bg)' }}>
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: 'var(--restore-border)', backgroundColor: 'rgba(255,255,255,0.03)' }}>
              <Zap className="h-3.5 w-3.5" style={{ color: 'var(--restore-accent)' }} />
              <p className="text-xs font-semibold" style={{ color: 'var(--restore-text-primary)' }}>
                {pendingActions.length} change{pendingActions.length !== 1 ? 's' : ''} queued — review before applying
              </p>
            </div>

            {/* Non-destructive changes */}
            {nonDestructive.length > 0 && (
              <div className="px-3 py-2 space-y-1.5">
                {nonDestructive.map((action, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <span className="mt-0.5 p-0.5 rounded flex-shrink-0" style={{ color: 'var(--restore-accent)', backgroundColor: 'rgba(var(--restore-accent-rgb,99,102,241),0.12)' }}>
                      {actionIcon(action.type)}
                    </span>
                    <p className="text-xs leading-snug" style={{ color: 'var(--restore-text-secondary)' }}>{action.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Destructive changes */}
            {destructive.length > 0 && (
              <div className={`px-3 py-2 space-y-1.5 ${nonDestructive.length > 0 ? 'border-t' : ''}`} style={{ borderColor: 'rgba(239,68,68,0.25)', backgroundColor: 'rgba(239,68,68,0.05)' }}>
                {nonDestructive.length === 0 && (
                  <div className="flex items-center gap-1.5 mb-1">
                    <AlertTriangle className="h-3 w-3 text-red-400 flex-shrink-0" />
                    <p className="text-xs font-medium text-red-400">Destructive — cannot be undone without undo</p>
                  </div>
                )}
                {destructive.map((action, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <Trash2 className="h-3 w-3 mt-0.5 flex-shrink-0 text-red-400" />
                    <p className="text-xs leading-snug" style={{ color: 'var(--restore-text-secondary)' }}>{action.label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2 px-3 py-2.5 border-t" style={{ borderColor: 'var(--restore-border)' }}>
              <Button
                size="sm"
                className="flex-1 h-7 text-xs font-medium"
                style={{ backgroundColor: 'var(--restore-accent)', color: '#0a0a0a', border: 0 }}
                onClick={handleApplyAll}
                disabled={executingActions}
              >
                {executingActions
                  ? <><Loader2 className="h-3 w-3 animate-spin mr-1.5" />Applying...</>
                  : <><CheckCircle2 className="h-3 w-3 mr-1.5" />Apply {pendingActions.length} change{pendingActions.length !== 1 ? 's' : ''}</>
                }
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 px-3 text-xs"
                style={{ color: 'var(--restore-text-muted)' }}
                onClick={handleCancelAll}
                disabled={executingActions}
              >
                Cancel
              </Button>
            </div>
          </div>
        )}

        {/* ── Undo strip (shown after apply, clears after 30s) ─────────────── */}
        {undoSnapshot && !isLoading && pendingActions.length === 0 && (
          <div className="flex items-center justify-between rounded-lg px-3 py-2" style={{ backgroundColor: 'rgba(var(--restore-accent-rgb,99,102,241),0.1)', border: '1px solid rgba(var(--restore-accent-rgb,99,102,241),0.25)' }}>
            <div className="flex items-center gap-1.5">
              <CheckCircle2 className="h-3.5 w-3.5" style={{ color: 'var(--restore-accent)' }} />
              <p className="text-xs" style={{ color: 'var(--restore-text-secondary)' }}>Changes applied to scope</p>
            </div>
            <button
              className="flex items-center gap-1 text-xs font-medium transition-opacity hover:opacity-70"
              style={{ color: 'var(--restore-accent)' }}
              onClick={handleUndo}
            >
              <RotateCcw className="h-3 w-3" />
              Undo
            </button>
          </div>
        )}

        {/* ── Navigation suggestion (from the navigate_to tool) ─────────────── */}
        {suggestedNavigation && !isLoading && (
          <div className="flex justify-start">
            <button
              className="flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 transition-opacity hover:opacity-80"
              style={{ backgroundColor: 'var(--restore-accent)', color: '#0a0a0a' }}
              onClick={() => {
                navigate(suggestedNavigation.path);
                setSuggestedNavigation(null);
              }}
            >
              <MapPin className="h-3 w-3" />
              Go to {suggestedNavigation.label}
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex-shrink-0 p-3 border-t" style={{ borderColor: 'var(--restore-border)', backgroundColor: 'var(--restore-card-bg)' }}>
        <div className="flex gap-2">
          <Textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Ask a question or tell me what to change..."
            className="flex-1 resize-none text-sm min-h-[40px] max-h-[120px]"
            style={{ backgroundColor: 'var(--restore-dark-bg)', borderColor: 'var(--restore-border)', color: 'var(--restore-text-primary)' }}
            rows={1}
            disabled={isLoading}
          />
          <Button
            size="sm"
            onClick={sendMessage}
            disabled={isLoading || !input.trim()}
            className="self-end h-10 w-10 p-0 flex-shrink-0"
            style={{ backgroundColor: 'var(--restore-accent)', color: '#0a0a0a' }}
          >
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <p className="text-xs" style={{ color: 'var(--restore-text-muted)' }}>
            Enter to send · Shift+Enter for new line
          </p>
          <div className="flex items-center gap-1" style={{ color: 'var(--restore-text-muted)' }}>
            <Globe className="h-3 w-3" />
            <span className="text-xs">Web search enabled</span>
          </div>
        </div>
      </div>
    </>
  );

  // ── Embedded (inline) variant ─────────────────────────────────────────────
  if (embedded) {
    return (
      <div
        className="flex flex-col rounded-xl border overflow-hidden"
        style={{ backgroundColor: 'var(--restore-dark-bg)', borderColor: 'var(--restore-border)', height: '460px' }}
      >
        {chatContent}
      </div>
    );
  }

  // ── Floating variant ──────────────────────────────────────────────────────
  // Rendered through a portal to document.body. Both the button and the panel
  // are position:fixed, but an ancestor with `transform`, `filter`, or
  // `will-change` becomes the containing block for fixed descendants, which
  // makes them scroll with the page instead of sticking to the viewport. The
  // workflow page wraps each stage in a framer-motion div that animates `y`
  // and `filter` (and retains `filter: blur(0px)` at rest — a zero-value
  // filter still creates a containing block), so portalling out is what keeps
  // this genuinely fixed.
  return createPortal(
    <>
      {/* Floating button */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-xl transition-all ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        style={{ backgroundColor: 'var(--restore-accent)', color: '#0a0a0a' }}
      >
        <Bot className="h-5 w-5" />
        <span className="font-semibold text-sm">Ask AI</span>
        <Badge className="ml-1 text-xs px-1.5 py-0.5 bg-black/20 text-inherit border-0">Beta</Badge>
      </button>

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-50 w-[420px] flex flex-col rounded-2xl shadow-2xl overflow-hidden"
          style={{ backgroundColor: 'var(--restore-dark-bg)', border: '1px solid var(--restore-border)', height: '600px' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b flex-shrink-0" style={{ borderColor: 'var(--restore-border)', backgroundColor: 'var(--restore-card-bg)' }}>
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg" style={{ backgroundColor: 'var(--restore-accent)' }}>
                <Sparkles className="h-4 w-4" style={{ color: '#0a0a0a' }} />
              </div>
              <div>
                <p className="font-semibold text-sm" style={{ color: 'var(--restore-text-primary)' }}>Project Assistant</p>
                <p className="text-xs" style={{ color: 'var(--restore-text-muted)' }}>{effectiveProject?.clientName || (effectiveProjectId ? 'Current project' : 'App help')}</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => {
                  endSession('clear');
                  setMessages([]);
                  setPendingActions([]);
                  setSuggestedNavigation(null);
                  localStorage.removeItem(STORAGE_KEY);
                }}
                className="p-1.5 rounded-lg transition-colors text-xs"
                title="Clear history"
                style={{ color: 'var(--restore-text-muted)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--restore-hover-bg)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <MessageSquare className="h-3.5 w-3.5" />
              </button>
              <button
                onClick={() => { endSession('close'); setIsOpen(false); }}
                className="p-1.5 rounded-lg transition-colors"
                style={{ color: 'var(--restore-text-muted)' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--restore-hover-bg)')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {chatContent}
        </div>
      )}
    </>,
    document.body,
  );
}
